import React, { useState } from "react";
import { Button } from "react-bootstrap";
import CollapsibleSection from "./CollapsibleSection";
import ExpandableTxTable, { TxColumn } from "./ExpandableTxTable";
import ReversePaymentModal from "./ReversePaymentModal";
import EnrollmentService from "../../../_services/EnrollmentService";
import { useAccessControl } from "../../../access-control/AccessControlContext";

interface PaymentDetailsSectionProps {
  /** Grouped-by-receipt transactions from plan-and-payments (paymentTransactions) */
  transactions: any[];
  loading: boolean;
  enrollmentId: number;
  onRefresh: () => void;
  /** Config-driven perms (resource 202 jsonConfig). undefined → fall back to ACL. */
  printReceiptPerm?: boolean;
  reversePaymentPerm?: boolean;
}

/** A reversal entry is the negative student_payment row (type 5 or reversed-from) */
const isReversalEntry = (t: any) =>
  t.paymentType === 5 || t.reversedFromPaymentId != null;

const PaymentDetailsSection: React.FC<PaymentDetailsSectionProps> = ({
  transactions,
  loading,
  enrollmentId,
  onRefresh,
  printReceiptPerm,
  reversePaymentPerm,
}) => {
  // Payment Details: regular (non-discount, non-reversal-entry) transactions.
  // Reversed originals STAY here, red-tinted (Java parity).
  const paymentRows = transactions.filter(
    (t: any) => t.paymentType !== 6 && !isReversalEntry(t),
  );
  // Reversals block: the reversal transaction entries.
  const reversalRows = transactions.filter(isReversalEntry);

  const [reverseTarget, setReverseTarget] = useState<any>(null);

  // Authorization (mirrors Java paymentPermissions). Permissive by default —
  // only restricts once an admin assigns roles to these elements in the ACL.
  const { canSeeElement } = useAccessControl();
  // Prefer the resource-202 jsonConfig perm when provided; else the ACL.
  const canPrintReceipt =
    printReceiptPerm !== undefined
      ? printReceiptPerm
      : canSeeElement("enrollment-view", "action.printReceipt");
  const canReversePayment =
    reversePaymentPerm !== undefined
      ? reversePaymentPerm
      : canSeeElement("enrollment-view", "action.reversePayment");

  const handlePrintReceipt = async (paymentId: number) => {
    // Render the receipt in NODE using the tenant's own Apache-Velocity template
    // (get-receipt.controller renders the same DB template the Java app uses and
    // returns print-ready HTML). Open the window synchronously to avoid popup
    // blockers, then write the rendered HTML into it.
    const printWin = window.open("", "_blank");
    if (!printWin) return;
    printWin.document.write(
      "<html><body><p>Loading receipt...</p></body></html>",
    );
    try {
      const res: any = await EnrollmentService.getReceipt(paymentId);
      if (res.data?.success && res.data.html) {
        printWin.document.open();
        printWin.document.write(res.data.html);
        printWin.document.close();
        printWin.focus();
      } else {
        printWin.document.open();
        printWin.document.write(
          `<html><body><p>${res.data?.message || "Failed to load receipt."}</p></body></html>`,
        );
        printWin.document.close();
      }
    } catch (err: any) {
      console.error("Error printing receipt:", err);
      printWin.document.open();
      printWin.document.write(
        "<html><body><p>Error loading receipt.</p></body></html>",
      );
      printWin.document.close();
    }
  };

  // Only show cheque columns when at least one payment carries cheque data
  const hasChequeData = paymentRows.some(
    (p: any) => p.instrumentIdentifier || p.instrumentDate,
  );

  const receiptCell = (p: any) => (
    <span style={{ whiteSpace: "nowrap" }}>
      {p.receiptNumber || "--"}
      {p.paymentId && canPrintReceipt && (
        <Button
          variant="secondary"
          size="sm"
          style={{ fontSize: 10, padding: "0px 4px", marginLeft: 4 }}
          onClick={(e) => {
            e.stopPropagation();
            handlePrintReceipt(p.paymentId);
          }}
          title="Print Receipt"
        >
          <i className="fa fa-print" />
        </Button>
      )}
    </span>
  );

  // ── Payment Details columns ─────────────────────────────────────────
  const paymentColumns: TxColumn[] = [
    { header: "Receipt #", cell: receiptCell, nowrap: true },
    { header: "Date", cell: (p) => p.paymentDate, nowrap: true },
    { header: "Instrument", cell: (p) => p.instrumentType },
    ...(hasChequeData
      ? ([
          {
            header: "Cheque #",
            cell: (p: any) => p.instrumentIdentifier || "--",
          },
          {
            header: "Cheque Date",
            cell: (p: any) => p.instrumentDate || "--",
            nowrap: true,
          },
        ] as TxColumn[])
      : []),
    { header: "Bank", cell: (p) => p.bankName },
    {
      header: "Amount",
      align: "right",
      cell: (p) => p.amount?.toLocaleString(),
    },
    {
      header: "Status",
      cell: (p) =>
        p.isReversed
          ? "Reversed"
          : p.statusText ||
            (p.statusId === 1 ? "Active" : p.statusId === 3 ? "Cleared" : "--"),
    },
    {
      header: "Action",
      width: 100,
      cell: (p) =>
        !p.isReversed && p.amount > 0 && canReversePayment ? (
          <Button
            variant="danger"
            size="sm"
            style={{
              fontSize: 10,
              padding: "1px 6px",
              backgroundColor: "var(--status-danger)",
              color: "var(--text-on-brand)",
              border: "1px solid var(--status-danger)",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setReverseTarget(p);
            }}
            title="Reverse Payment"
          >
            Reverse
          </Button>
        ) : null,
    },
  ];

  // ── Reversals columns ───────────────────────────────────────────────
  const reversalColumns: TxColumn[] = [
    { header: "Receipt #", cell: (p) => p.receiptNumber || "--", nowrap: true },
    { header: "Date", cell: (p) => p.paymentDate, nowrap: true },
    { header: "Instrument", cell: (p) => p.instrumentType },
    {
      header: "Amount",
      align: "right",
      cell: (p) => p.amount?.toLocaleString(),
    },
  ];

  return (
    <>
      <CollapsibleSection title="Payment Details" count={paymentRows.length}>
        {loading ? (
          <div className="ev-loading">
            <i className="fa fa-spinner fa-spin" /> Loading payments...
          </div>
        ) : paymentRows.length === 0 ? (
          <div className="ev-empty">No payment details</div>
        ) : (
          <ExpandableTxTable
            rows={paymentRows}
            columns={paymentColumns}
            rowClassName={(p) => (p.isReversed ? "ev-reversed-row" : undefined)}
          />
        )}
      </CollapsibleSection>

      {reversalRows.length > 0 && (
        <CollapsibleSection
          title="Reversals"
          count={reversalRows.length}
          defaultOpen={false}
        >
          <ExpandableTxTable
            rows={reversalRows}
            columns={reversalColumns}
            rowClassName={() => "ev-reversed-row"}
            detailFooterExtra={(p) =>
              p.cancelledRemarks ? (
                <span>
                  {" · "}Reversal Remarks: <b>{p.cancelledRemarks}</b>
                </span>
              ) : null
            }
          />
        </CollapsibleSection>
      )}

      {reverseTarget && (
        <ReversePaymentModal
          show={!!reverseTarget}
          onHide={() => setReverseTarget(null)}
          enrollmentId={enrollmentId}
          payment={reverseTarget}
          onSuccess={() => {
            setReverseTarget(null);
            onRefresh();
          }}
        />
      )}
    </>
  );
};

export default PaymentDetailsSection;
